import { Transaction } from "@/app/entities/Transaction";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";
import { PartnerType } from "@/app/utils/partners.enum";
import { onlyFirstName } from "@/app/utils/onlyFirstName";
import Status from "@/view/components/Status";
import { Button } from "@/view/components/ui/button";
import { Spinner } from "@/view/components/ui/spinner";
import { useMemo } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { PartnerImage } from "@/view/components/PartnerImage";

interface Props {
  transactions: Transaction[];
  onClick: (transaction: Transaction) => void;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  products?: ProductCatalogItem[];
}

export function MobileTransactionList({
  transactions,
  onClick,
  onLoadMore,
  isLoadingMore,
  hasMore,
  products,
}: Props) {
  const formatTime = (date: string | Date) => {
    return new Date(date).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDateKey = (date: Date) => {
    if (isToday(date)) return "Hoje";
    if (isYesterday(date)) return "Ontem";

    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };

  const groupedTransactions = useMemo(() => {
    return transactions.reduce(
      (acc, transaction) => {
        if (!transaction?.createdAt) {
          acc["Sem data"] = acc["Sem data"] || [];
          acc["Sem data"].push(transaction);
          return acc;
        }

        const date = new Date(transaction.createdAt);
        const dateKey = getDateKey(date);

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(transaction);

        return acc;
      },
      {} as Record<string, Transaction[]>,
    );
  }, [transactions]);

  return (
    <div className="flex flex-col gap-3">
      {Object.entries(groupedTransactions).map(([date, items]) => (
        <div key={date} className="flex flex-col gap-2">
          <div className="px-1 text-sm font-semibold text-gray-500">{date}</div>
          {items.map((transaction) => {
            return (
              <button
                key={transaction._id}
                onClick={() => onClick(transaction)}
                className="flex items-center justify-between rounded-xl border p-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <PartnerImage
                    type={transaction.type as PartnerType}
                    products={products}
                  />
                  <div>
                    <p className="font-medium">
                      {onlyFirstName(
                        transaction.customer?.name || "Sem cliente",
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <p>{formatTime(transaction.createdAt)}</p>
                      <Status status={transaction.actualStatus} onlyIcon />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-semibold">
                    {formatMoneyValue(transaction.value)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatMoneyValue(transaction.commission)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      ))}

      {hasMore && (
        <div className="flex py-4">
          <Button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            variant={"outline"}
            className="w-[160px] rounded-full text-primary"
          >
            {isLoadingMore ? <Spinner /> : "Carregar mais"}
          </Button>
        </div>
      )}
    </div>
  );
}
